import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import Forms from '../common/forms';

export default class FindResale {
    constructor(mapElementId) {
        this.section = document.querySelector('.component-find-resale');
        this.apiKey = document.querySelector('meta[name="maps:key"]')?.getAttribute('content') || '';
        this.mapElementId = mapElementId || 'map';
        this.map = null;
        this.markers = [];
        this.markerCluster = null;
        this.mapDistance = 5000;
        this.buscaRealizada = false;
        this.cache = new Map();

        this.containerList = this.section.querySelector('#containerList');
        this.distributorList = this.section.querySelector('#distribuidorList');

        this.ready();
        this.setupGeolocationButton();
        this.setupSearchButton();

        if (window.innerWidth < 768) {
            this.changeListMode();
        }
    }

    ready() {
        this.initMap().then(() => {
            this.centerMapByZoom();

            let debounceTimeout = null;
            let firstIdleSkipped = false;

            this.map.addListener('idle', () => {
                if (!firstIdleSkipped) {
                    firstIdleSkipped = true;
                    return;
                }

                const input = this.section.querySelector('#local');
                const inputValido = input && input.value.trim().length > 0;

                if (!this.buscaRealizada || !inputValido) return;

                clearTimeout(debounceTimeout);
                debounceTimeout = setTimeout(() => {
                    this.updateMarkersByMap();
                }, 800);
            });
        });
    }

    async initMap(center = { lat: -14.235, lng: -70.9253 }, zoom = 4) {
        try {
            if (!window.google) {
                await this.loadGoogleMaps();
            }

            this.map = new google.maps.Map(document.getElementById(this.mapElementId), {
                center,
                zoom,
                mapTypeControl: false,
                streetViewControl: false,
            });

            this.markerCluster = new MarkerClusterer({
                markers: this.markers,
                map: this.map,
            });
        } catch (error) {
            console.error('Erro ao carregar o Google Maps:', error);
        }
    }

    async loadGoogleMaps() {
        const loader = new Loader({
            apiKey: this.apiKey,
            version: 'weekly',
            libraries: ['places', 'geometry'],
        });
        return loader.load();
    }

    async updateMarkersByMap() {
        if (!this.map) return;
        const center = this.map.getCenter();
        const radius = this.getCurrentMapRadius();
        this.mapDistance = radius;

        const cacheKey = `${center.lat().toFixed(4)},${center.lng().toFixed(4)},${radius}`;
        let resalesData = this.cache.get(cacheKey);

        if (!resalesData) {
            resalesData = await this.fetchResalesData(center.lat(), center.lng(), radius);
            this.cache.set(cacheKey, resalesData);
        }

        this.clearMarkers();

        resalesData.forEach((resale) => {
            const lat = Number(resale.latitude);
            const lng = Number(resale.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                this.addMarker({ lat, lng }, resale.nome, resale.email, resale.telefone);
            }
        });

        this.updateDistributorList(resalesData);
    }

    getCurrentMapRadius() {
        if (!this.map) return this.mapDistance;
        const bounds = this.map.getBounds();
        if (!bounds) return this.mapDistance;
        const center = this.map.getCenter();
        const ne = bounds.getNorthEast();
        const distance = google.maps.geometry.spherical.computeDistanceBetween(center, ne);
        return Math.min(distance, 50000);
    }

    async fetchResalesData(lat, lng, radius = this.mapDistance) {
        try {
            const response = await fetch(`/api/resales?lat=${lat}&lng=${lng}&radius=${radius}`);
            if (!response.ok) throw new Error('Erro ao buscar dados das revendas');
            return await response.json();
        } catch (error) {
            console.error('Erro ao buscar revendas:', error);
            return [];
        }
    }

    clearMarkers() {
        this.markers.forEach((marker) => marker.setMap(null));
        this.markers = [];
        if (this.markerCluster) {
            this.markerCluster.clearMarkers();
        }
    }

    addMarker(location, title = '', email = '', telefone = '') {
        if (!this.currentInfoWindow) this.currentInfoWindow = null;

        const marker = new google.maps.Marker({
            position: location,
            title,
            icon: '/images/pin.png',
            animation: google.maps.Animation.DROP,
        });

        const directionsUrl = `https://www.google.com/maps/dir//${location.lat},${location.lng}`;
        const contentString = this.templateTipResale(title, email, telefone, directionsUrl);
        const infoWindow = new google.maps.InfoWindow({ content: contentString });

        marker.addListener('click', () => {
            if (this.currentInfoWindow) this.currentInfoWindow.close();
            infoWindow.open(this.map, marker);
            this.currentInfoWindow = infoWindow;
        });

        this.markers.push(marker);
        if (this.markerCluster) this.markerCluster.addMarker(marker);
    }

    setupSearchButton() {
        const form = this.section.querySelector('#searchResaleForm');
        const btn = this.section.querySelector('.btnSearchResales');
        const errorForm = this.section.querySelector('.error-form');
        const input = this.section.querySelector('#local');
        const erroCampo = this.section.querySelector('#local_error');

        if (!form || !btn) return;

        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            if (!input || !input.value.trim()) {
                input?.classList.add('error-field');
                erroCampo?.classList.remove('hidden');
                input?.focus();
                return;
            }

            input.classList.remove('error-field');
            erroCampo?.classList.add('hidden');
            errorForm?.classList.add('hidden');

            Forms.setLoading(btn, true);

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address: input.value.trim() }, async (results, status) => {
                if (status === 'OK' && results[0]) {
                    this.buscaRealizada = true;
                    const loc = results[0].geometry.location;

                    this.map.panTo(loc);
                    this.map.setZoom(16);

                    const resalesData = await this.fetchResalesData(loc.lat(), loc.lng());
                    this.clearMarkers();

                    resalesData.forEach((resale) => {
                        const lat = Number(resale.latitude);
                        const lng = Number(resale.longitude);
                        if (!isNaN(lat) && !isNaN(lng)) {
                            this.addMarker({ lat, lng }, resale.nome, resale.email, resale.telefone);
                        }
                    });

                    this.updateDistributorList(resalesData);

                    const resultCount = resalesData.length;
                    if (resultCount > 0) {
                        const resultLabel = this.section.querySelector('#resultadosLabel');
                        if (resultLabel) {
                            resultLabel.textContent = `${resultCount} ${
                                resultCount === 1 ? 'resultado encontrado' : 'resultados encontrados'
                            }`;
                        }
                    }
                } else {
                    console.error('Geocoder falhou:', status);
                    if (errorForm) {
                        errorForm.classList.remove('hidden');
                        errorForm.focus();
                        errorForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                }

                Forms.setLoading(btn, false);
            });
        });
    }

    updateDistributorList(resalesData) {
        this.distributorList.innerHTML = '';
        resalesData.forEach((resale) => {
            const template = this.templateShowDistribuitors(
                resale.latitude,
                resale.longitude,
                resale.nome,
                resale.email,
                resale.telefone
            );
            const div = document.createElement('div');
            div.innerHTML = template;

            div.addEventListener('click', () => {
                const position = new google.maps.LatLng(resale.latitude, resale.longitude);
                this.map.panTo(position);
                this.map.setZoom(18);

                const directionsUrl = `https://www.google.com/maps/dir//${resale.latitude},${resale.longitude}`;
                const contentString = this.templateTipResale(resale.nome, resale.email, resale.telefone, directionsUrl);

                if (this.currentInfoWindow) this.currentInfoWindow.close();
                const infoWindow = new google.maps.InfoWindow({ content: contentString });
                const marker = this.markers.find((m) => m.getTitle() === resale.nome);
                if (marker) infoWindow.open(this.map, marker);
                this.currentInfoWindow = infoWindow;
            });

            this.distributorList.appendChild(div);
        });
        this.containerList.classList.add('active');
    }

    setupGeolocationButton() {
        const btn = document.getElementById('btnGetMyLocation');
        if (!btn) return;

        btn.addEventListener('click', () => {
            btn.classList.add('hidden');
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async (pos) => {
                        const userLoc = {
                            lat: pos.coords.latitude,
                            lng: pos.coords.longitude,
                        };

                        const geocoder = new google.maps.Geocoder();
                        geocoder.geocode({ location: userLoc }, (results, status) => {
                            if (status === 'OK' && results[0]) {
                                const address = results[0].formatted_address;
                                if (address) {
                                    this.section.querySelector('#local').value = address;
                                    this.buscaRealizada = true;
                                    this.section.querySelector('.btnSearchResales').click();
                                }
                            } else {
                                console.error('Geocoder falhou:', status);
                            }
                        });
                    },
                    (error) => console.error('Erro na geolocalização:', error)
                );
            }
        });
    }

    centerMapByZoom(right = 0.2) {
        const center = this.map.getCenter();
        if (center) {
            const zoom = this.map.getZoom();
            const lngOffset = right / Math.pow(2, zoom - 10);
            const offsetCenter = { lat: center.lat(), lng: center.lng() - lngOffset };
            this.map.setCenter(offsetCenter);
        }
    }

    templateShowDistribuitors(latitude, longitude, name, email, phone, address, site, states) {
        return `
            <div class="flex flex-row gap-3">
                <div class="block w-full max-w-[30px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="30" fill="none"><path fill="#FBC600" d="M13 17.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z"/><path fill="#FBC600" d="m21.485 21.485-6.364 6.364a3 3 0 0 1-4.24 0l-6.367-6.364a12 12 0 1 1 16.971 0Z"/><path stroke="#FBC600" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z"/><path stroke="#FBC600" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21.485 21.485-6.364 6.364a3 3 0 0 1-4.24 0l-6.367-6.364a12 12 0 1 1 16.971 0Z"/><circle cx="13" cy="13" r="4.5" fill="#fff"/></svg>
                </div>
                <div class="flex w-full max-w-[305px] gap-4">
                    <div class="flex flex-col gap-1">
                        ${name ? `<p class="text-sm/extra font-medium text-shellcinza">${name}</p>` : ''}
                        ${address ? `<p class="text-sm/extra text-shellcinza">${address}</p>` : ''}
                        ${email ? `<p class="text-sm/extra text-shellcinza">${email}</p>` : ''}
                        ${phone ? `<p class="text-sm/extra text-shellcinza">${phone}</p>` : ''}
                        ${
                            site
                                ? `<a href="${site}" target="_blank" class="text-sm/extra text-shellcinza hover:underline underline-offset-4">${site}</a>`
                                : ''
                        }
                        ${states ? `<p class="text-sm/extra text-shellcinza">Estados atendidos: ${states}</p>` : ''}
                        ${
                            latitude
                                ? `<p class="text-sm/extra text-shellcinza">
                                <a href="https://www.google.com/maps/dir//${latitude},${longitude}" target="_blank" class="text-sm/extra font-medium text-shellcinza flex flex-row gap-2 items-center hover:text-shellred">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6.5 10.167V8.5c0-.492.345-.833.833-.833H11.5m0 0L9.833 6M11.5 7.667 9.833 9.333M7.75 15.5 2 9.75c-.651-.651-.651-1.85 0-2.5L7.75 1.5c.65-.651 1.849-.651 2.5 0L16 7.25c.65.65.65 1.849 0 2.5l-5.75 5.75c-.651.65-1.85.65-2.5 0Z"/></svg> <span>Como chegar</span>
                                </a>
                            </p>`
                                : ''
                        }
                    </div>
                </div>
            </div>
        `;
    }

    templateTipResale(name, email, phone, url) {
        return `<div class="w-full max-w-[384px] p-4 bg-white shadow-md" style="width:384px;">
            <div class="flex flex-row gap-3">
                <div class="block w-full max-w-[30px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="28" fill="none"><path fill="#DD1D21" d="m14.121 26.85 6.364-6.365a12 12 0 1 0-16.97 0L9.88 26.85a3 3 0 0 0 4.241 0Z"/><path fill="#fff" d="M17 11a5 5 0 1 1-10 0 5 5 0 0 1 10 0Z"/></svg>
                </div>
                <div class="flex w-full max-w-[305px] gap-4">
                    <div class="flex flex-col gap-1">
                        ${name ? `<p class="text-sm/extra font-medium text-shellcinza">${name}</p>` : ''}
                        ${email ? `<p class="text-sm/extra text-shellcinza">${email}</p>` : ''}
                        ${phone ? `<p class="text-sm/extra text-shellcinza">${phone}</p>` : ''}
                        ${
                            url
                                ? `<p class="text-sm/extra text-shellcinza"><a href="${url}" target="_blank" class="text-sm/extra font-medium text-shellcinza flex flex-row gap-2 items-center hover:text-shellred"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6.5 10.167V8.5c0-.492.345-.833.833-.833H11.5m0 0L9.833 6M11.5 7.667 9.833 9.333M7.75 15.5 2 9.75c-.651-.651-.651-1.85 0-2.5L7.75 1.5c.65-.651 1.849-.651 2.5 0L16 7.25c.65.65.65 1.849 0 2.5l-5.75 5.75c-.651.65-1.85.65-2.5 0Z"/></svg> <span>Como chegar</span></a></p>`
                                : ''
                        }
                    </div>
                </div>
            </div>
        </div>`;
    }

    changeListMode() {
        const btnList = this.section.querySelector('#btnShowList');
        const btnMap = this.section.querySelector('#btnShowMap');

        btnList.addEventListener('click', () => {
            btnMap.classList.remove('active');
            this.section.querySelector('#map').classList.add('hidden');
            btnList.classList.add('active');
            this.containerList.classList.add('active');
        });

        btnMap.addEventListener('click', () => {
            btnList.classList.remove('active');
            this.containerList.classList.remove('active');
            btnMap.classList.add('active');
            this.section.querySelector('#map').classList.remove('hidden');
        });
    }
}

if (document.querySelector('.component-find-resale')) {
    new FindResale();
}
